import { Injectable, inject } from '@angular/core';

import { Sitecore, TopNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { TopMenuContentItem } from '@frontend/sports/navigation/core/feature/models';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { NavigationBarItem } from '@frontend/sports/types/models/navigation-bar';
import { DslService, MediaQueryService } from '@frontend/vanilla/core';
import { linkedTransferState, tapIfServer } from '@frontend/vanilla/ssr';
import produce from 'immer';
import { Observable, combineLatest, map, startWith, switchMap } from 'rxjs';

import { NavigationItemsMapper } from '../navigation-items-mapper';
import { BaseNavigationItemsProvider } from './base-navigation-items.provider';

@Injectable({ providedIn: 'root' })
export class TopNavigationItemsProvider extends BaseNavigationItemsProvider {
    private navigationConfig = inject(TopNavigationConfig);
    private dslService = inject(DslService);
    private mapper = inject(NavigationItemsMapper);
    private urlHelper = inject(UrlHelperService);
    private sitecore = inject(Sitecore);
    private layoutService = inject(AdaptiveLayoutService);
    private mediaQueryService = inject(MediaQueryService);

    readonly navigationItems$: Observable<TopMenuContentItem[]>;
    readonly navigationItemsTransferState = linkedTransferState<TopMenuContentItem[]>('top-navigation-items', []);

    constructor() {
        super();

        const siteCoreReady$ = this.sitecore.whenReady;

        const evaluatedItems$ = this.navigationConfig.whenReady.pipe(
            map(() =>
                this.navigationConfig.topNavigationItems?.length ? this.navigationConfig.topNavigationItems : this.navigationConfig.beforeSports,
            ),
            switchMap((data) => this.dslService.evaluateContent(data)),
        );

        const liveSportCount$ = this.getLiveSportsList();

        this.navigationItems$ = combineLatest([evaluatedItems$, this.hasLiveItems$, liveSportCount$, siteCoreReady$]).pipe(
            map(([items, keepLiveItem, liveSportCount]) => {
                const liveCountText = this.formatLiveCounts(liveSportCount);
                const options = {
                    keepLiveItem,
                    isFixedItem: false,
                };

                const liveItem = items.filter((item) => item.activeName === 'live');
                if (this.isLiveButtonBadgeVisible(liveItem) && liveSportCount > 0) {
                    liveItem[0].badgeText = liveCountText;
                }
                const isV2Navigation = !!this.navigationConfig.topNavigationItems?.length;

                const itemsToReturn = isV2Navigation
                    ? this.mapper.mapItems(items, liveCountText, options)
                    : this.getFallbackItems(items, liveCountText, keepLiveItem);

                return produce([], () => itemsToReturn);
            }),
            tapIfServer((items) => this.navigationItemsTransferState.set(items)),
            startWith(this.navigationItemsTransferState()),
        );
    }

    public getDefaultItems(): Observable<TopMenuContentItem[]> {
        const isDesktop = this.mediaQueryService.isActive('gt-md');
        const home = this.getHomeMenuItem();

        return this.hasLiveItems$.pipe(
            map((keepLive) => {
                const items: TopMenuContentItem[] = [];

                if (isDesktop && home) {
                    items.push(home);
                }

                if (keepLive) {
                    items.push({
                        name: 'live',
                        text: this.sitecore.globalMessages.NavLive,
                        url: this.urlHelper.getLiveUrl(),
                        class: 'theme-live',
                        trackEvent: this.mapper.getTrackingData('live'),
                        parameters: {},
                        resources: {},
                    } as TopMenuContentItem);
                }

                items.push({
                    name: 'all',
                    text: this.sitecore.globalMessages.NavAllSports,
                    url: isDesktop ? '' : this.urlHelper.getAtoZUrl(),
                    class: 'theme-menu',
                    trackEvent: this.mapper.getTrackingData('all'),
                    parameters: {},
                    resources: {},
                    popup: isDesktop ? 'sports' : '',
                } as TopMenuContentItem);

                return items;
            }),
        );
    }

    private getFallbackItems(items: NavigationBarItem[], sportsCountText: string, keepLiveItem: boolean): TopMenuContentItem[] {
        const home = this.getHomeMenuItem();
        const beforeSports = this.mapper.mapItems(items, sportsCountText, { keepLiveItem });

        return home ? [home, ...beforeSports] : [...beforeSports];
    }

    // TODO: should be removed when only V2 is used on prod.
    getHomeMenuItem(): TopMenuContentItem | undefined {
        if (this.layoutService.current.bottomNavigation) {
            return;
        }

        return {
            name: 'home',
            text: this.sitecore.globalMessages.NavHome,
            url: this.urlHelper.getHomeUrl(),
            class: 'theme-home',
            trackEvent: this.mapper.getTrackingData('home'),
            parameters: {},
            resources: {},
        } as TopMenuContentItem;
    }

    getLiveSportsList(): Observable<number> {
        return this.sportsCache.getSportList().pipe(map((sportList) => sportList.map((s) => s.counts.live).reduce((acc, count) => acc + count, 0)));
    }

    formatLiveCounts(liveCount: number) {
        let counts = '';
        if (liveCount > 99) {
            counts = '99+';
        } else if (liveCount > 0) {
            counts = liveCount.toString();
        }

        return counts;
    }

    isLiveButtonBadgeVisible(liveItem: NavigationBarItem[]) {
        if (liveItem?.length && liveItem[0]?.showBadge) {
            return true;
        }

        return false;
    }
}
